import { useCallback, useEffect, useRef, useState } from 'react';
import { callBackend } from '../utils/api';
import { canonicalJsonObject } from '../utils/optimizerConstraintEditor';

const requireBaseline = data => {
    if (data?.contract !== 'optimizer-baseline.v1') throw new Error('Baseline statistics could not be loaded.');
    return data;
};

// A prepared exact-key read joins the existing publication transaction; ordinary
// edits keep their coalesced read. Immutable displayed runs are separate.
export default function useOptimizerBaseline({ enabled, inputKey, buildCommand, canRead = () => true }) {
    const [state, setState] = useState({ key: null, data: null, loading: false, error: null });
    const buildRef = useRef(buildCommand), canReadRef = useRef(canRead);
    buildRef.current = buildCommand; canReadRef.current = canRead;
    const generationRef = useRef(0), preparedSeedRef = useRef(null);
    const [retry, setRetry] = useState(0);
    const retryRef = useRef(retry); retryRef.current = retry;
    useEffect(() => {
        if (!enabled || !canReadRef.current()) return undefined;
        const seed = preparedSeedRef.current;
        if (seed?.key === inputKey && seed.retry === retry) return undefined;
        preparedSeedRef.current = null;
        let cancelled = false;
        const generation = ++generationRef.current;
        const isCurrent = () => !cancelled && generationRef.current === generation && canReadRef.current();
        const controller = new AbortController();
        setState({ key: inputKey, data: null, loading: true, error: null });
        const timer = setTimeout(async () => {
            try {
                if (!isCurrent()) return;
                const command = await buildRef.current();
                if (!isCurrent()) return;
                const data = requireBaseline(await callBackend('optimizer_baseline_v2', 'POST',
                    canonicalJsonObject(command), { signal: controller.signal }));
                if (isCurrent()) setState({ key: inputKey, data, loading: false, error: null });
            } catch (err) {
                const message = err.code === 'optimizer_run_v2_contract_failed'
                    ? `The portfolio, Watchlist and selected factor data could not be checked together. Refresh the app, then retry.${err.reference ? ` Reference: ${err.reference}.` : ''}`
                    : err.message;
                if (isCurrent()) setState({ key: inputKey, data: null, loading: false, error: message });
            }
        }, 150);
        return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
    }, [enabled, inputKey, retry]);
    const prepare = useCallback(async ({ inputKey: key, enabled: targetEnabled, buildCommand: build,
        isCurrentRequest, stagePublicationState }) => {
        if (typeof isCurrentRequest !== 'function' || typeof stagePublicationState !== 'function'
            || typeof build !== 'function' || typeof key !== 'string' || typeof targetEnabled !== 'boolean') {
            throw new Error('Prepared Optimizer baseline requires explicit owned inputs.');
        }
        const capturedRetry = retryRef.current;
        const isCurrent = () => retryRef.current === capturedRetry && isCurrentRequest();
        if (!isCurrent()) throw new Error('Optimizer baseline preparation was superseded.');
        let data = null;
        if (targetEnabled) {
            const command = await build();
            if (!isCurrent()) throw new Error('Optimizer baseline preparation was superseded.');
            data = requireBaseline(await callBackend('optimizer_baseline_v2', 'POST', canonicalJsonObject(command)));
            if (!isCurrent()) throw new Error('Optimizer baseline preparation was superseded.');
        }
        stagePublicationState(() => {
            generationRef.current += 1;
            preparedSeedRef.current = { key, retry: capturedRetry };
            setState({ key, data, loading: false, error: null });
        }, isCurrent);
        return data;
    }, []);
    return {
        data: enabled && state.key === inputKey ? state.data : null,
        loading: enabled && (state.key !== inputKey || state.loading),
        error: enabled && state.key === inputKey ? state.error : null,
        retry: () => setRetry(value => value + 1), prepare,
    };
}
